// /shadow/subscripciones-rbl — Manage R₿L subscriptions (subscriber + analyst views).
//
// Shows:
//  - Active R₿L subscriptions you have (with cancel button per item)
//  - R₿L subscribers you receive (analyst view) + month income
//  - Billing history per subscription
//
// Stripe/€ subscriptions are NOT shown here — they live in /shadow/monetiza.
// This page is purely the R₿L payment rail.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Coins, Crown, X, Loader2, History, TrendingUp, AlertTriangle } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const I18N = {
  es: {
    back: "// SHADOW NETWORK",
    eyebrow: "// SUSCRIPCIONES R₿L",
    title: "Tus suscripciones R₿L",
    sub: "Gestiona tus suscripciones pagadas con tokens R₿L. El flujo en € se gestiona en /shadow/monetiza.",
    login_sub: "Inicia sesión para ver tus suscripciones R₿L.",
    login_btn: "Iniciar sesión",
    loading: "Cargando…",
    section_as_sub: "Como suscriptor",
    section_as_analyst: "Suscriptores R₿L",
    section_income: "Ingreso del mes",
    empty_as_sub: "Aún no tienes suscripciones R₿L activas.",
    empty_as_analyst: "Aún no tienes suscriptores R₿L.",
    explore_analysts: "Explorar analistas →",
    monthly: "/mes",
    started: "Iniciada",
    next_billing: "Próximo cobro",
    last_billed: "Último cobro",
    history: "Historial de cobros",
    history_empty: "Sin cobros aún.",
    cancel: "Cancelar",
    cancel_confirm: "¿Cancelar esta suscripción R₿L? No se reembolsa el mes en curso.",
    cancel_busy: "Cancelando…",
    status_active: "Activa",
    status_failed: "Cobro fallido",
    status_cancelled: "Cancelada",
    failed_grace: "Tu balance R₿L no cubre la renovación. Tienes 48h para recargar o se cancelará.",
    income_this_month: "ingresados este mes",
    rows_amount: "Monto",
    rows_date: "Fecha",
    rows_status: "Estado",
  },
  en: {
    back: "// SHADOW NETWORK",
    eyebrow: "// R₿L SUBSCRIPTIONS",
    title: "Your R₿L subscriptions",
    sub: "Manage subscriptions paid with R₿L tokens. The EUR flow lives at /shadow/monetiza.",
    login_sub: "Sign in to see your R₿L subscriptions.",
    login_btn: "Sign in",
    loading: "Loading…",
    section_as_sub: "As subscriber",
    section_as_analyst: "R₿L subscribers",
    section_income: "Income this month",
    empty_as_sub: "No active R₿L subscriptions yet.",
    empty_as_analyst: "No R₿L subscribers yet.",
    explore_analysts: "Explore analysts →",
    monthly: "/mo",
    started: "Started",
    next_billing: "Next billing",
    last_billed: "Last billed",
    history: "Billing history",
    history_empty: "No charges yet.",
    cancel: "Cancel",
    cancel_confirm: "Cancel this R₿L subscription? Current month is not refunded.",
    cancel_busy: "Cancelling…",
    status_active: "Active",
    status_failed: "Charge failed",
    status_cancelled: "Cancelled",
    failed_grace: "Your R₿L balance can't cover renewal. You have 48h to top up or it will be cancelled.",
    income_this_month: "received this month",
    rows_amount: "Amount",
    rows_date: "Date",
    rows_status: "Status",
  },
  sk: {
    back: "// SHADOW NETWORK",
    eyebrow: "// PREDPLATNÉ R₿L",
    title: "Tvoje predplatné R₿L",
    sub: "Spravuj predplatné platené v R₿L tokenoch. EUR tok je v /shadow/monetiza.",
    login_sub: "Prihlás sa pre prezeranie predplatného R₿L.",
    login_btn: "Prihlásiť sa",
    loading: "Načítava sa…",
    section_as_sub: "Ako predplatiteľ",
    section_as_analyst: "Predplatitelia R₿L",
    section_income: "Príjem mesiaca",
    empty_as_sub: "Zatiaľ žiadne aktívne predplatné R₿L.",
    empty_as_analyst: "Zatiaľ žiadni predplatitelia R₿L.",
    explore_analysts: "Preskúmať analytikov →",
    monthly: "/mes.",
    started: "Začaté",
    next_billing: "Ďalšia platba",
    last_billed: "Posledná platba",
    history: "História platieb",
    history_empty: "Zatiaľ žiadne platby.",
    cancel: "Zrušiť",
    cancel_confirm: "Zrušiť toto predplatné R₿L? Bežný mesiac sa nevracia.",
    cancel_busy: "Ruším…",
    status_active: "Aktívne",
    status_failed: "Platba zlyhala",
    status_cancelled: "Zrušené",
    failed_grace: "Tvoj R₿L balance nepokryje obnovu. Máš 48h na doplnenie, inak sa zruší.",
    income_this_month: "prijaté tento mesiac",
    rows_amount: "Suma",
    rows_date: "Dátum",
    rows_status: "Stav",
  }, de: { back: "// SHADOW NETWORK", eyebrow: "// R₿L-ABONNEMENTS", title: "Deine R₿L-Abonnements", sub: "Verwalte deine mit R₿L-Token bezahlten Abonnements. Der €-Fluss wird unter /shadow/monetiza verwaltet.", login_sub: "Melde dich an, um deine R₿L-Abonnements zu sehen.", login_btn: "Anmelden", loading: "Lädt…", section_as_sub: "Als Abonnent", section_as_analyst: "R₿L-Abonnenten", section_income: "Einnahmen des Monats", empty_as_sub: "Du hast noch keine aktiven R₿L-Abonnements.", empty_as_analyst: "Du hast noch keine R₿L-Abonnenten.", explore_analysts: "Analysten entdecken →", monthly: "/Monat", started: "Gestartet", next_billing: "Nächste Abrechnung", last_billed: "Letzte Abrechnung", history: "Abrechnungsverlauf", history_empty: "Noch keine Abrechnungen.", cancel: "Kündigen", cancel_confirm: "Dieses R₿L-Abonnement kündigen? Der laufende Monat wird nicht erstattet.", cancel_busy: "Wird gekündigt…", status_active: "Aktiv", status_failed: "Abrechnung fehlgeschlagen", status_cancelled: "Gekündigt", failed_grace: "Dein R₿L-Guthaben reicht nicht für die Verlängerung. Du hast 48h zum Aufladen, sonst wird es gekündigt.", income_this_month: "diesen Monat eingegangen", rows_amount: "Betrag", rows_date: "Datum", rows_status: "Status" }, "zh-CN": { back: "// SHADOW NETWORK", eyebrow: "// R₿L 订阅", title: "你的 R₿L 订阅", sub: "管理你用 R₿L 代币支付的订阅。€ 流程在 /shadow/monetiza 中管理。", login_sub: "登录以查看你的 R₿L 订阅。", login_btn: "登录", loading: "加载中…", section_as_sub: "作为订阅者", section_as_analyst: "R₿L 订阅者", section_income: "本月收入", empty_as_sub: "你还没有活跃的 R₿L 订阅。", empty_as_analyst: "你还没有 R₿L 订阅者。", explore_analysts: "探索分析师 →", monthly: "/月", started: "开始时间", next_billing: "下次扣费", last_billed: "上次扣费", history: "扣费历史", history_empty: "暂无扣费记录。", cancel: "取消", cancel_confirm: "取消此 R₿L 订阅？当月费用不予退还。", cancel_busy: "取消中…", status_active: "活跃", status_failed: "扣费失败", status_cancelled: "已取消", failed_grace: "你的 R₿L 余额不足以续订。你有 48 小时充值，否则将被取消。", income_this_month: "本月已入账", rows_amount: "金额", rows_date: "日期", rows_status: "状态" },
};
const tr = (lang, k) => (I18N[lang] || I18N.en || I18N.es)[k] || I18N.es[k] || k;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export default function ShadowSubscriptionsRblPage() {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();
  const [data, setData] = useState({ as_subscriber: [], as_analyst: [], analyst_month_income_rbl: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user || !token) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/shadow/subscriptions/rbl`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await safeJson(r);
      if (r.ok && j) setData(j);
    } catch (_e) { /* non-critical */ }
    finally { setLoading(false); }
  }, [user, token]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (subId) => {
    if (busyId) return;
    if (!window.confirm(tr(lang, "cancel_confirm"))) return;
    setBusyId(subId);
    try {
      const r = await fetch(`${API}/shadow/subscriptions/rbl/${subId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rbl-subs-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link
          to="/shadow"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-amber-300"
          data-testid="rbl-subs-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {tr(lang, "back")}
        </Link>

        <div className="mt-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
            {tr(lang, "eyebrow")}
          </p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
            {tr(lang, "title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">{tr(lang, "sub")}</p>
        </div>

        {!user ? (
          <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.025] p-8 text-center backdrop-blur" data-testid="rbl-subs-login">
            <p className="text-sm text-white/55">{tr(lang, "login_sub")}</p>
            <button
              onClick={openLogin}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black"
              data-testid="rbl-subs-login-btn"
            >
              {tr(lang, "login_btn")}
            </button>
          </div>
        ) : loading ? (
          <div className="mt-12 flex items-center justify-center text-white/45">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {tr(lang, "loading")}
          </div>
        ) : (
          <>
            {/* Month income — analyst-side summary, only when there is at least one R₿L subscriber or any history. */}
            {(data.analyst_month_income_rbl > 0 || (data.as_analyst || []).length > 0) && (
              <div
                className="mt-8 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.08] to-orange-500/[0.04] p-6 backdrop-blur"
                data-testid="rbl-subs-income"
              >
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-200/80">
                  {tr(lang, "section_income")}
                </p>
                <p className="mt-1 font-mono text-5xl font-black text-amber-200">
                  <TrendingUp className="inline mb-2 mr-2 h-8 w-8 text-amber-300" />
                  {(data.analyst_month_income_rbl || 0).toLocaleString()} <span className="text-2xl">R₿L</span>
                </p>
                <p className="mt-1 text-xs text-white/55">{tr(lang, "income_this_month")}</p>
              </div>
            )}

            {/* As subscriber */}
            <div className="mt-10" data-testid="rbl-subs-as-subscriber">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/75">
                {tr(lang, "section_as_sub")}
              </h2>
              {(data.as_subscriber || []).length === 0 ? (
                <div className="mt-3 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
                  <p className="text-sm text-white/55">{tr(lang, "empty_as_sub")}</p>
                  <Link
                    to="/shadow/ranking"
                    className="mt-3 inline-block text-xs font-mono uppercase tracking-wider text-amber-300/80 hover:text-amber-200"
                  >
                    {tr(lang, "explore_analysts")}
                  </Link>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-1 gap-3">
                  {data.as_subscriber.map((s) => (
                    <SubCard
                      key={s.id}
                      sub={s}
                      lang={lang}
                      onCancel={cancel}
                      busy={busyId === s.id}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* As analyst */}
            {(data.as_analyst || []).length > 0 && (
              <div className="mt-10" data-testid="rbl-subs-as-analyst">
                <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/75">
                  {tr(lang, "section_as_analyst")}
                </h2>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {data.as_analyst.map((s) => (
                    <div
                      key={s.id}
                      className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04] p-4"
                      data-testid={`rbl-analyst-sub-${s.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-200">
                          <Crown className="h-3.5 w-3.5" />
                          @{s.subscriber_username || "operator"}
                        </span>
                        <StatusBadge status={s.status} lang={lang} />
                      </div>
                      <p className="mt-1.5 font-mono text-lg font-black text-white">
                        {s.amount_rbl_monthly?.toLocaleString()} <span className="text-xs">R₿L{tr(lang, "monthly")}</span>
                      </p>
                      <p className="mt-1 text-[10px] text-white/45">
                        {tr(lang, "next_billing")}: {fmtDate(s.next_billing_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

const StatusBadge = ({ status, lang }) => {
  const map = {
    active: { cls: "border-emerald-400/40 bg-emerald-400/[0.08] text-emerald-200", label: tr(lang, "status_active") },
    failed: { cls: "border-amber-400/40 bg-amber-400/[0.08] text-amber-200", label: tr(lang, "status_failed") },
    cancelled: { cls: "border-white/15 bg-white/[0.04] text-white/55", label: tr(lang, "status_cancelled") },
  }[status] || { cls: "border-white/15 bg-white/[0.04] text-white/55", label: status };
  return (
    <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${map.cls}`}>
      {map.label}
    </span>
  );
};

const SubCard = ({ sub, lang, onCancel, busy }) => {
  const [open, setOpen] = useState(false);
  const isActive = sub.status === "active";
  const isFailed = sub.status === "failed";
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur"
      data-testid={`rbl-sub-${sub.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-300" />
            <Link
              to={`/shadow/perfil/${sub.analyst_username || ""}`}
              className="font-bold text-white hover:text-amber-300"
            >
              @{sub.analyst_username || "analyst"}
            </Link>
            <StatusBadge status={sub.status} lang={lang} />
          </div>
          <p className="mt-1.5 font-mono text-xl font-black text-white">
            {sub.amount_rbl_monthly?.toLocaleString()}{" "}
            <span className="text-amber-300 text-sm">R₿L{tr(lang, "monthly")}</span>
            <span className="ml-2 text-xs text-white/40">≈ €{sub.eur_equivalent_at_creation}</span>
          </p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] font-mono text-white/45">
            <span>{tr(lang, "started")}: {fmtDate(sub.started_at)}</span>
            <span>{tr(lang, "next_billing")}: {fmtDate(sub.next_billing_at)}</span>
            <span>{tr(lang, "last_billed")}: {fmtDate(sub.last_billed_at)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {(isActive || isFailed) && (
            <button
              onClick={() => onCancel(sub.id)}
              disabled={busy}
              data-testid={`rbl-sub-cancel-${sub.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-400/[0.05] px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-red-300 hover:border-red-400/60 hover:bg-red-400/[0.1] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              {busy ? tr(lang, "cancel_busy") : tr(lang, "cancel")}
            </button>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-white/45 hover:text-white"
          >
            <History className="h-3 w-3" /> {tr(lang, "history")} ({(sub.billing_history || []).length})
          </button>
        </div>
      </div>

      {isFailed && (
        <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-200">
          <AlertTriangle className="inline h-3 w-3 mr-1" />
          {tr(lang, "failed_grace")}
        </div>
      )}

      {open && (
        <div className="mt-3 rounded-lg border border-white/8 bg-black/30 p-3" data-testid={`rbl-sub-history-${sub.id}`}>
          {(sub.billing_history || []).length === 0 ? (
            <p className="text-[11px] text-white/40">{tr(lang, "history_empty")}</p>
          ) : (
            <table className="w-full font-mono text-[10.5px]">
              <thead>
                <tr className="text-white/35">
                  <th className="text-left py-1 font-normal">{tr(lang, "rows_date")}</th>
                  <th className="text-right py-1 font-normal">{tr(lang, "rows_amount")}</th>
                </tr>
              </thead>
              <tbody>
                {[...sub.billing_history].reverse().map((h, i) => (
                  <tr key={i} className="border-t border-white/5 text-white/65">
                    <td className="py-1">{fmtDate(h.billed_at)}</td>
                    <td className="py-1 text-right text-amber-300">{h.amount_rbl?.toLocaleString()} R₿L</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
